import { useState, useEffect } from 'react'
import {
  Bot, BarChart3, Wallet, Library, Rocket, Settings,
  CheckCircle, XCircle, AlertTriangle, ExternalLink,
  Activity, Target, Loader2, Zap,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { cn } from '@/lib/utils'
import { truncateAddress } from '@/lib/utils'
import { useSeedbotHealth, SEEDBOT_BASE_URL, type SeedbotHealthData } from '@/hooks/useSeedbotHealth'
import { useSeedbotLaunches } from '@/hooks/useSeedbotLaunches'

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

function estimateNextLaunch(uptime: number, launchesPerHour: number): string {
  if (launchesPerHour <= 0) return 'N/A'
  const intervalMin = 60 / launchesPerHour
  const uptimeMin = uptime / 60
  const elapsed = uptimeMin % intervalMin
  const remaining = Math.max(0, Math.round(intervalMin - elapsed))
  return `~${remaining}m`
}

type TrendingPipeline = SeedbotHealthData['trendingPipeline']

function getContentSource(pipeline?: TrendingPipeline): string {
  if (!pipeline) return '...'
  if (pipeline.cachedTopicCount > 0) return 'AI + Trending'
  if (pipeline.lastAIConcept) return 'AI Generic'
  return 'Static Library'
}

function formatTickerStyles(tickerStyles?: { style: string; count: number; percentage: number }[]): string {
  if (!tickerStyles || tickerStyles.length === 0) return '...'
  return tickerStyles.map((t) => `${t.style} (${t.percentage}%)`).join(', ')
}

function formatLastAIConcept(concept?: { name: string; ticker: string; trendTopic?: string }): string {
  if (!concept) return 'None'
  let result = `${concept.name} ($${concept.ticker})`
  if (concept.trendTopic) result += ` from "${concept.trendTopic}"`
  return result
}

export function SeedbotPage() {
  const { data: health, isLoading: healthLoading, error: healthError } = useSeedbotHealth()
  const { data: launches, isLoading: launchesLoading } = useSeedbotLaunches()

  // "Last updated Xs ago" counter
  const [secondsAgo, setSecondsAgo] = useState(0)
  useEffect(() => {
    if (!health?.fetchedAt) return
    setSecondsAgo(0)
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - health.fetchedAt) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [health?.fetchedAt])

  // Parse success rate for color coding
  const successRateNum = health
    ? parseFloat(health.stats.successRate)
    : 0

  const successRateColor = successRateNum >= 90
    ? 'text-green-500'
    : successRateNum >= 70
      ? 'text-yellow-500'
      : 'text-red-500'

  // Content library calculations
  const totalConcepts = health?.contentLibrary.totalConcepts || 210
  const usedThisSession = health?.stats.totalAttempts || 0
  const remaining = Math.max(0, totalConcepts - (usedThisSession % totalConcepts))
  const launchesPerHour = health?.scheduler.launchesPerHour || 4
  const depletionHours = launchesPerHour > 0 ? Math.round(remaining / launchesPerHour) : 0

  // Total wallet SOL
  const totalSol = health?.wallets.reduce(
    (sum, w) => sum + parseFloat(w.balanceSol),
    0,
  ) || 0

  // Launch mode toggle
  const [launchMode, setLaunchMode] = useState<'auto' | 'custom'>('auto')

  // Manual launch state (auto mode)
  const [devBuySol, setDevBuySol] = useState('0.01')
  const [launching, setLaunching] = useState(false)
  const [launchResult, setLaunchResult] = useState<{
    success: boolean
    message: string
    mintAddress?: string
  } | null>(null)

  // Custom launch state
  const [customName, setCustomName] = useState('')
  const [customSymbol, setCustomSymbol] = useState('')
  const [customDescription, setCustomDescription] = useState('')
  const [customImage, setCustomImage] = useState<File | null>(null)
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(null)
  const [customWebsite, setCustomWebsite] = useState('')
  const [customTwitter, setCustomTwitter] = useState('')
  const [customTelegram, setCustomTelegram] = useState('')
  const [customDevBuy, setCustomDevBuy] = useState('0.01')

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      setCustomImage(file)
      const reader = new FileReader()
      reader.onload = () => setCustomImagePreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const allWalletsLow = health?.wallets.every((w) => w.isLow) ?? true
  const canLaunch = !!health && !healthError
  const devBuyNum = parseFloat(devBuySol)
  const devBuyValid = !isNaN(devBuyNum) && devBuyNum >= 0

  const customValid = customName.length > 0 && customSymbol.length > 0
    && customDescription.length > 0 && customImage !== null

  async function handleDextoroLaunch() {
    if (!devBuyValid) return
    setLaunching(true)
    setLaunchResult(null)
    try {
      const url = new URL(`${SEEDBOT_BASE_URL}/trigger-launch`)
      if (devBuyNum > 0) {
        url.searchParams.set('devBuySol', devBuyNum.toString())
      }
      const res = await fetch(url.toString(), { method: 'POST' })
      const data = await res.json()
      if (!res.ok) {
        const errorMsg = res.status === 409
          ? 'Launch already in progress'
          : res.status === 503
            ? 'Seedbot still initializing'
            : data.error || 'Launch failed'
        setLaunchResult({ success: false, message: errorMsg })
        return
      }
      if (data.success) {
        setLaunchResult({
          success: true,
          message: `Launched ${data.name ?? 'coin'} ($${data.symbol ?? '???'})`,
          mintAddress: data.mintAddress,
        })
      } else {
        setLaunchResult({
          success: false,
          message: data.error || 'Launch failed',
        })
      }
    } catch (err) {
      setLaunchResult({
        success: false,
        message: err instanceof Error ? err.message : 'Network error',
      })
    } finally {
      setLaunching(false)
    }
  }

  async function handleCustomLaunch() {
    if (!customValid || !customImage) return
    setLaunching(true)
    setLaunchResult(null)
    try {
      const formData = new FormData()
      formData.append('image', customImage)
      formData.append('name', customName)
      formData.append('symbol', customSymbol)
      formData.append('description', customDescription)
      if (customWebsite) formData.append('websiteUrl', customWebsite)
      if (customTwitter) formData.append('twitterUrl', customTwitter)
      if (customTelegram) formData.append('telegramUrl', customTelegram)
      const customDevBuyNum = parseFloat(customDevBuy)
      if (!isNaN(customDevBuyNum) && customDevBuyNum > 0) {
        formData.append('devBuySol', customDevBuyNum.toString())
      }

      const res = await fetch(`${SEEDBOT_BASE_URL}/trigger-launch-custom`, {
        method: 'POST',
        body: formData,
      })
      const data = await res.json()
      if (!res.ok) {
        setLaunchResult({ success: false, message: data.error || 'Custom launch failed' })
        return
      }
      if (data.success) {
        setLaunchResult({
          success: true,
          message: `Launched ${data.name ?? customName} ($${data.symbol ?? customSymbol})`,
          mintAddress: data.mintAddress,
        })
      } else {
        setLaunchResult({ success: false, message: data.error || 'Launch failed' })
      }
    } catch (err) {
      setLaunchResult({ success: false, message: err instanceof Error ? err.message : 'Network error' })
    } finally {
      setLaunching(false)
    }
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-brand-fg" />
            Seedbot
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Autonomous coin launcher operations
          </p>
        </div>
        {health && (
          <span className="text-xs text-muted-foreground">
            Updated {secondsAgo}s ago
          </span>
        )}
      </div>

      {/* Health error banner */}
      {healthError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
          <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-500">
            Unable to reach seedbot. Service may be down.
          </span>
        </div>
      )}

      {/* Section 1: Status Banner */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Seedbot Status
              </span>
              {healthLoading ? (
                <span className="h-5 w-20 bg-muted rounded animate-pulse" />
              ) : health ? (
                <span className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium',
                  health.scheduler.running
                    ? 'bg-green-500/10 text-green-500'
                    : 'bg-red-500/10 text-red-500',
                )}>
                  <span className={cn(
                    'h-2 w-2 rounded-full',
                    health.scheduler.running ? 'bg-green-500' : 'bg-red-500',
                  )} />
                  {health.scheduler.running ? 'Running' : 'Stopped'}
                </span>
              ) : null}
            </div>
            {health && (
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Uptime: <span className="text-foreground font-medium">{formatUptime(health.uptime)}</span></span>
                <span>Rate: <span className="text-foreground font-medium">{health.scheduler.launchesPerHour}/hour</span></span>
                <span>Next launch: <span className="text-foreground font-medium">{estimateNextLaunch(health.uptime, health.scheduler.launchesPerHour)}</span></span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual Launch */}
      <div>
        <DashboardSectionHeader icon={Zap} title="Manual Launch" />
        <Card>
          <CardContent className="p-4 sm:p-6">
            {/* Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={launchMode === 'auto' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setLaunchMode('auto'); setLaunchResult(null) }}
              >
                Auto Launch
              </Button>
              <Button
                variant={launchMode === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setLaunchMode('custom'); setLaunchResult(null) }}
              >
                Custom Launch
              </Button>
            </div>

            {launchMode === 'auto' ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Trigger a single dextoro coin launch. The bot will pick the next available wallet and generate a new coin.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
                  <div className="w-full sm:w-40">
                    <Label htmlFor="dexDevBuySol" className="text-xs text-muted-foreground mb-1.5 block">
                      Dev Buy (SOL)
                    </Label>
                    <Input
                      id="dexDevBuySol"
                      type="number"
                      min="0"
                      step="0.01"
                      value={devBuySol}
                      onChange={(e) => setDevBuySol(e.target.value)}
                      className={cn(!devBuyValid && devBuySol !== '' && 'border-red-500')}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-3 mb-4">
                <p className="text-sm text-muted-foreground">
                  Launch with custom branding. Upload your own logo, name, ticker, and description.
                </p>

                {/* Image Upload */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Token Logo</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand-fg/10 file:text-brand-fg hover:file:bg-brand-fg/20"
                    />
                    {customImagePreview && (
                      <img src={customImagePreview} alt="Preview" className="h-10 w-10 rounded-md object-cover border border-border" />
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Token Name</Label>
                    <Input
                      placeholder="My Coin"
                      maxLength={32}
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                    />
                  </div>
                  <div className="w-full sm:w-32">
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Ticker</Label>
                    <Input
                      placeholder="COIN"
                      maxLength={6}
                      value={customSymbol}
                      onChange={(e) => setCustomSymbol(e.target.value.toUpperCase())}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Description</Label>
                  <textarea
                    placeholder="the next big thing on solana, no cap"
                    maxLength={200}
                    rows={2}
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{customDescription.length}/200</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Website URL</Label>
                    <Input placeholder="https://..." value={customWebsite} onChange={(e) => setCustomWebsite(e.target.value)} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Twitter/X URL</Label>
                    <Input placeholder="https://x.com/..." value={customTwitter} onChange={(e) => setCustomTwitter(e.target.value)} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Telegram URL</Label>
                    <Input placeholder="https://t.me/..." value={customTelegram} onChange={(e) => setCustomTelegram(e.target.value)} />
                  </div>
                </div>

                <div className="w-full sm:w-40">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Dev Buy (SOL)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={customDevBuy}
                    onChange={(e) => setCustomDevBuy(e.target.value)}
                  />
                </div>
              </div>
            )}

            {allWalletsLow && health && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-sm text-amber-500">
                  All wallets have low balances. Launch may fail.
                </span>
              </div>
            )}

            <Button
              onClick={launchMode === 'auto' ? handleDextoroLaunch : handleCustomLaunch}
              disabled={!canLaunch || launching || (launchMode === 'auto' ? !devBuyValid : !customValid)}
              className="w-full sm:w-auto"
            >
              {launching ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Launching...
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4 mr-2" />
                  {launchMode === 'auto' ? 'Launch Coin' : 'Launch Custom Coin'}
                </>
              )}
            </Button>

            {launchResult && (
              <div className={cn(
                'mt-4 rounded-lg border p-3 text-sm',
                launchResult.success
                  ? 'border-green-500/30 bg-green-500/10 text-green-500'
                  : 'border-red-500/30 bg-red-500/10 text-red-500'
              )}>
                {launchResult.success ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 shrink-0" />
                      <span>{launchResult.message}</span>
                    </div>
                    {launchResult.mintAddress && (
                      <a
                        href={`https://launch.dextoro.com/token/${launchResult.mintAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-fg hover:underline text-xs ml-6"
                      >
                        View on dextoro
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 shrink-0" />
                    <span>{launchResult.message}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 2: Performance */}
      <div>
        <DashboardSectionHeader icon={BarChart3} title="Performance" />
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Launches"
            value={health?.stats.totalAttempts ?? 0}
            icon={Activity}
            loading={healthLoading}
          />
          <StatsCard
            title="Successes"
            value={health?.stats.totalSuccesses ?? 0}
            icon={CheckCircle}
            loading={healthLoading}
          />
          <StatsCard
            title="Failures"
            value={health?.stats.totalFailures ?? 0}
            icon={XCircle}
            loading={healthLoading}
          />
          <StatsCard
            title="Success Rate"
            value={health?.stats.successRate || '0.0%'}
            icon={Target}
            loading={healthLoading}
            className={health ? `[&_p.font-bold]:${successRateColor}` : undefined}
          />
        </div>
      </div>

      {/* Section 3: Wallet Balances */}
      <div>
        <DashboardSectionHeader icon={Wallet} title="Wallet Balances" />
        <Card>
          <CardContent className="p-0">
            {healthLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-8 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : health?.wallets && health.wallets.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground">Address</th>
                    <th className="text-right p-3 sm:p-4 text-xs font-medium text-muted-foreground">Balance</th>
                    <th className="text-right p-3 sm:p-4 text-xs font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {health.wallets.map((wallet) => (
                    <tr key={wallet.address} className="border-b border-border last:border-b-0">
                      <td className="p-3 sm:p-4 font-mono text-xs">
                        {truncateAddress(wallet.address, 4)}
                      </td>
                      <td className="p-3 sm:p-4 text-right font-medium">
                        {parseFloat(wallet.balanceSol).toFixed(4)} SOL
                      </td>
                      <td className="p-3 sm:p-4 text-right">
                        <span className={cn(
                          'inline-flex items-center gap-1.5 text-xs font-medium',
                          wallet.isLow ? 'text-red-500' : 'text-green-500',
                        )}>
                          <span className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            wallet.isLow ? 'bg-red-500' : 'bg-green-500',
                          )} />
                          {wallet.isLow ? 'Low' : 'Healthy'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/30">
                    <td className="p-3 sm:p-4 text-xs font-medium text-muted-foreground">TOTAL</td>
                    <td className="p-3 sm:p-4 text-right font-bold">{totalSol.toFixed(4)} SOL</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No wallet data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 4: Content Library */}
      <div>
        <DashboardSectionHeader icon={Library} title="Content Library" />
        <Card>
          <CardContent className="p-4 sm:p-6">
            {healthLoading ? (
              <div className="flex gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-12 w-32 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-6 sm:gap-8">
                <div>
                  <p className="text-xs text-muted-foreground">Total Concepts</p>
                  <p className="text-xl font-bold">{totalConcepts}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Used This Session</p>
                  <p className="text-xl font-bold">{usedThisSession}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Remaining</p>
                  <p className="text-xl font-bold">{remaining}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Est. Depletion</p>
                  <p className="text-xl font-bold">~{depletionHours}h</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 5: Recent Launches */}
      <div>
        <DashboardSectionHeader icon={Rocket} title="Recent Launches" />
        <Card>
          <CardContent className="p-0">
            {launchesLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-8 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : launches && launches.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground">Name</th>
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground">Ticker</th>
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground">Creator</th>
                    <th className="text-right p-3 sm:p-4 text-xs font-medium text-muted-foreground">Time</th>
                    <th className="text-right p-3 sm:p-4 text-xs font-medium text-muted-foreground">Mint</th>
                  </tr>
                </thead>
                <tbody>
                  {launches.map((launch) => (
                    <tr key={launch.mint_address} className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors">
                      <td className="p-3 sm:p-4 font-medium">{launch.name}</td>
                      <td className="p-3 sm:p-4 font-mono text-xs">{launch.symbol}</td>
                      <td className="p-3 sm:p-4 text-muted-foreground">@{launch.creator_username}</td>
                      <td className="p-3 sm:p-4 text-right text-muted-foreground">
                        {formatTimeAgo(launch.created_at)}
                      </td>
                      <td className="p-3 sm:p-4 text-right">
                        <a
                          href={`https://launch.dextoro.com/token/${launch.mint_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-brand-fg hover:underline font-mono text-xs"
                        >
                          {truncateAddress(launch.mint_address, 4)}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No launches found
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 6: Current Configuration */}
      <div>
        <DashboardSectionHeader icon={Settings} title="Current Configuration" />
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ConfigRow label="Auto-launch" value={health?.autoLaunch?.dextoro ? 'Enabled' : health ? 'Disabled' : '...'} />
              <ConfigRow label="Atomic Create+Buy" value={health?.bundleConfig?.dextoroEnabled ? 'Enabled' : health ? 'Disabled' : '...'} />
              <ConfigRow label="Launches/Hour" value={health ? String(health.scheduler.launchesPerHour) : '...'} />
              <ConfigRow label="FAL.ai Model" value={health?.config?.falModel ?? '...'} />
              <ConfigRow label="Content Source" value={getContentSource(health?.trendingPipeline)} />
              {/* Trending Sources: clickable actor badges */}
              <div className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
                <span className="text-xs text-muted-foreground">Trending Sources</span>
                <div className="flex flex-wrap gap-1.5 justify-end">
                  {health?.trendingPipeline?.actors && health.trendingPipeline.actors.length > 0 ? (
                    health.trendingPipeline.actors.map((actor) => (
                      <button
                        key={actor.id}
                        onClick={() => window.open(actor.apifyUrl, '_blank')}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-fg/10 text-brand-fg hover:bg-brand-fg/20 cursor-pointer transition-colors"
                      >
                        {actor.name}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    ))
                  ) : (
                    <span className="text-sm font-medium">None</span>
                  )}
                </div>
              </div>
              <ConfigRow label="Last AI Concept" value={formatLastAIConcept(health?.trendingPipeline?.lastAIConcept)} />
              <ConfigRow label="Style Variations" value={health?.config ? `${health.config.styleVariationCount} (randomized per launch)` : '...'} />
              <ConfigRow label="Ticker Styles" value={formatTickerStyles(health?.config?.tickerStyles)} />
              <ConfigRow label="Vanity Suffix" value={health?.config ? `"${health.config.vanitySuffix}"` : '...'} />
              <div className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
                <span className="text-xs text-muted-foreground">IPFS Gateway</span>
                <div className="text-right">
                  <span className="text-sm font-medium">{health?.config?.ipfsGateway ?? '...'}</span>
                  {health?.config?.ipfsGateway && (
                    <span className="block text-xs text-muted-foreground">(via Supabase edge function)</span>
                  )}
                </div>
              </div>
              <ConfigRow label="Min Wallet Balance" value={health?.config ? `${health.config.minWalletBalance} SOL` : '...'} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  )
}
